import { Box, Button, Link, Typography } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';

import { VerificationStatus } from 'tools/constants/payment';

import {
  FooterType,
  getDescriptionMap,
  getStatusConfig,
  VerificationFlow,
} from './verificationStatusConfig';

// ─── Types ───────────────────────────────────────────────────────────────────
export interface TransactionParams {
  redirectUrl?: string;
  status:
    | VerificationStatus.LOADING
    | VerificationStatus.SUCCESS
    | VerificationStatus.ERROR
    | VerificationStatus.CANCEL
    | VerificationStatus.TIMEOUT
    | VerificationStatus.INVALID;
  transactionID: string;
}

interface TransactionVerificationProps {
  /**
   * Async function that fetches the current transaction status.
   * Return a VerificationStatus (excluding 'loading' / 'error' / 'timeout').
   * When undefined the component stays in its `initialStatus`
   */
  fetchStatus?: (transactionID: string) => Promise<VerificationStatus>;
  /** Which flow is being verified — controls the displayed strings. Default: 'transaction' */
  flow?: VerificationFlow;
  /**
   * Override the initial status — useful for connecting real API data later.
   * Defaults to 'loading' when params are valid.
   */
  initialStatus?: VerificationStatus;
  /**
   * Called when the component wants to perform a redirect.
   * Defaults to `window.location.assign`.
   */
  onRedirect?: (url: string) => void;
  /**
   * Parsed and validated query parameters. Pass `null` to trigger the
   * invalid-params error state.
   */
  params: TransactionParams | null;
  /** How often (ms) the status should be polled. Default: 3000 */
  pollIntervalMs?: number;
  /** Maximum time (ms) before timing out. Default: 30000 */
  pollTimeoutMs?: number;
  /** Seconds to wait before auto-redirecting after a terminal status. Default: 5 */
  redirectCountdownSecs?: number;
}

/** Statuses where verification is complete and the user should be redirected. */
const RESOLVED_STATUSES: VerificationStatus[] = [
  VerificationStatus.SUCCESS,
  VerificationStatus.ERROR,
  VerificationStatus.CANCEL,
  VerificationStatus.TIMEOUT,
];

const DEFAULT_POLL_INTERVAL_MS = 3_000;
const DEFAULT_POLL_TIMEOUT_MS = 30_000;
const DEFAULT_REDIRECT_COUNTDOWN_SECS = 5;

/** Status to resolve to when polling exhausts without an answer, by flow. */
const POLL_TIMEOUT_STATUS_BY_FLOW: Partial<
  Record<VerificationFlow, VerificationStatus>
> = {
  [VerificationFlow.ADD_PAYMENT_METHOD]: VerificationStatus.ERROR,
};

const DEFAULT_ON_REDIRECT = (url: string): void => window.location.assign(url);

const dotPulse = keyframes`
  0%, 80%, 100% { opacity: 0.2; transform: scale(0.8); }
  40%           { opacity: 1;   transform: scale(1);   }
`;

/** Appends ?status=<action> to the redirect URL unless it is already present. */
const buildRedirectUrl = (base: string, status: VerificationStatus): string => {
  try {
    const url = new URL(base);
    if (!url.searchParams.has('status')) {
      url.searchParams.set('status', status);
    }
    return url.toString();
  } catch {
    return base;
  }
};

const countdownText = (countdown: number) =>
  `Redirecting in ${countdown} ${countdown === 1 ? 'second' : 'seconds'}…`;

interface StatusDisplayProps {
  countdown: number;
  flow: VerificationFlow;
  onRetry: () => void;
  redirectUrl: string;
  status: VerificationStatus;
}

const StatusDisplay = ({
  status,
  countdown,
  flow,
  redirectUrl,
  onRetry,
}: StatusDisplayProps) => {
  const statusConfig = getStatusConfig(flow);
  const descriptionMap = getDescriptionMap(flow);
  const config = statusConfig[status];

  const description =
    config.description ||
    `${descriptionMap[status] || ''} ${countdownText(countdown)}`;

  return (
    <Box alignItems="center" display="flex" flexDirection="column" gap={3}>
      {config.icon}

      <Box textAlign="center">
        <Typography fontWeight={600} gutterBottom variant="h5">
          {config.title}
        </Typography>

        {status === VerificationStatus.LOADING && (
          <Box
            aria-hidden
            display="flex"
            gap="6px"
            justifyContent="center"
            mb={1.5}
          >
            {[0, 1, 2].map((i) => (
              <Box
                key={i}
                sx={{
                  animation: `${dotPulse} 1.4s ease-in-out ${
                    i * 0.16
                  }s infinite`,
                  backgroundColor: 'text.disabled',
                  borderRadius: '50%',
                  height: 8,
                  width: 8,
                }}
              />
            ))}
          </Box>
        )}

        <Typography color="text.secondary" variant="body2">
          {description}
        </Typography>
      </Box>

      {config.footer === FooterType.LOADING && (
        <Typography
          color="warning.main"
          sx={{ fontSize: '0.75rem', fontWeight: 500 }}
        >
          Please do not refresh or go back.
        </Typography>
      )}

      {config.footer === FooterType.REDIRECT && redirectUrl && (
        <Link href={redirectUrl} underline="hover" variant="body2">
          If you are not redirected, click here
        </Link>
      )}

      {config.footer === FooterType.RETRY && (
        <Button onClick={onRetry} variant="contained">
          Go Back
        </Button>
      )}
    </Box>
  );
};

const TransactionVerification = ({
  params,
  initialStatus,
  flow = VerificationFlow.TRANSACTION,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
  pollTimeoutMs = DEFAULT_POLL_TIMEOUT_MS,
  redirectCountdownSecs = DEFAULT_REDIRECT_COUNTDOWN_SECS,
  onRedirect = DEFAULT_ON_REDIRECT,
  fetchStatus,
}: TransactionVerificationProps) => {
  const resolvedInitial: VerificationStatus = params
    ? (initialStatus ?? VerificationStatus.LOADING)
    : VerificationStatus.INVALID;

  const [status, setStatus] = useState<VerificationStatus>(resolvedInitial);
  const [countdown, setCountdown] = useState(redirectCountdownSecs);

  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearPolling = () => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
  };

  const clearCountdown = () => {
    if (countdownRef.current) clearInterval(countdownRef.current);
  };

  // Post message to mobile app
  const handlePostMessage = (status: VerificationStatus) => {
    switch (status) {
      case VerificationStatus.SUCCESS:
        window.OnSuccess?.postMessage?.(status);
        break;
      case VerificationStatus.ERROR:
        window.OnError?.postMessage?.(status);
        break;
      case VerificationStatus.CANCEL:
        window.OnCancel?.postMessage?.(status);
        break;
      case VerificationStatus.TIMEOUT:
        window.OnTimeout?.postMessage?.(status);
        break;
    }
  };

  // Start redirect countdown once a resolved status is reached
  useEffect(() => {
    if (!RESOLVED_STATUSES.includes(status) || !params) return;

    setCountdown(redirectCountdownSecs);
    let remaining = redirectCountdownSecs;

    countdownRef.current = setInterval(() => {
      remaining -= 1;
      setCountdown(remaining);
      if (remaining <= 0) {
        clearCountdown();
        if (params?.redirectUrl) {
          onRedirect(buildRedirectUrl(params.redirectUrl, status));
        } else {
          handlePostMessage(status);
        }
      }
    }, 1_000);

    return clearCountdown;
  }, [status, params, redirectCountdownSecs, onRedirect]);

  // Poll for status when fetchStatus is provided
  useEffect(() => {
    if (!params || !fetchStatus) return;

    const poll = async () => {
      try {
        const result = await fetchStatus(params.transactionID);
        setStatus((current) => {
          // Don't overwrite an already-resolved status
          if (RESOLVED_STATUSES.includes(current)) return current;
          if (RESOLVED_STATUSES.includes(result)) {
            clearPolling();
          }
          return result;
        });
      } catch {
        // keep polling; individual fetch errors are transient
      }
    };

    poll();
    pollIntervalRef.current = setInterval(poll, pollIntervalMs);

    pollTimeoutRef.current = setTimeout(() => {
      clearPolling();
      setStatus((current) => {
        if (RESOLVED_STATUSES.includes(current)) return current;
        return POLL_TIMEOUT_STATUS_BY_FLOW[flow] ?? VerificationStatus.TIMEOUT;
      });
    }, pollTimeoutMs);

    return clearPolling;
  }, [params, fetchStatus, pollIntervalMs, pollTimeoutMs, flow]);

  const handleRetry = () => {
    clearCountdown();
    window.history.back();
  };

  const redirectUrl =
    params && RESOLVED_STATUSES.includes(status)
      ? buildRedirectUrl(params.redirectUrl ?? '', status)
      : '#';

  return (
    <Box
      alignItems="center"
      display="flex"
      justifyContent="center"
      minHeight="100vh"
      px={{ xs: 2, sm: 4 }}
      py={6}
      sx={{ overflowX: 'hidden' }}
    >
      <Box
        sx={{
          border: '1px solid',
          borderColor: ({ palette }) => palette.divider,
          borderRadius: '12px',
          maxWidth: 480,
          padding: { xs: '32px 24px', sm: '48px' },
          textAlign: 'center',
          width: '100%',
        }}
      >
        <StatusDisplay
          countdown={countdown}
          flow={flow}
          onRetry={handleRetry}
          redirectUrl={redirectUrl}
          status={status}
        />
      </Box>
    </Box>
  );
};

export default TransactionVerification;
